import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GraduationCap, BookOpen, Users, Plus, Trash2, Pencil, CheckCircle2, XCircle, Loader2, X, Copy, ChevronRight, PlayCircle, Clock, Upload, BarChart3, TrendingUp, DollarSign, Award, ShieldCheck, IdCard } from 'lucide-react';

const EMPTY_LESSON = { title: '', video_type: 'youtube', video_url: '', video_storage_path: '', pdf_url: '', duration_min: 15, is_post_recorded: false, session_date: '' };
const EMPTY_QUESTION = { q: '', options: ['', '', '', ''], correct_index: 0 };
const EMPTY_COURSE = {
  title: '', description: '', category: 'trading', level: 'beginner',
  fee_amount: 199, duration_weeks: 4, instructor: '', thumbnail_url: '',
  syllabus: [''], lessons: [ { ...EMPTY_LESSON } ], exam_questions: [ { ...EMPTY_QUESTION } ],
  pass_percentage: 60, status: 'published',
};

const CATEGORIES = ['trading', 'portfolio', 'forex', 'crypto', 'other'];
const LEVELS = ['beginner', 'intermediate', 'advanced'];

const statusColor = (s) => ({
  payment_pending: 'bg-amber-100 text-amber-700',
  active: 'bg-brand-success/15 text-brand-success',
  completed: 'bg-brand-primary/15 text-brand-primary',
  cancelled: 'bg-slate-100 text-slate-500',
}[s] || 'bg-slate-100 text-slate-500');

export default function AcademyAdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('courses');

  // Courses
  const [courses, setCourses] = useState([]);
  const [courseDialog, setCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE);
  const [savingCourse, setSavingCourse] = useState(false);

  // Enrollments
  const [enrollments, setEnrollments] = useState([]);
  const [enrollFilter, setEnrollFilter] = useState('all');
  const [processing, setProcessing] = useState('');

  // Students
  const [students, setStudents] = useState([]);
  const [createStudentDialog, setCreateStudentDialog] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '', phone: '', send_email: true });
  const [createdCred, setCreatedCred] = useState(null);
  const [creatingStudent, setCreatingStudent] = useState(false);

  const [loading, setLoading] = useState(true);
  const [uploadingIndex, setUploadingIndex] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analytics, setAnalytics] = useState(null);

  // KYC review
  const [kycPending, setKycPending] = useState([]);
  const [kycProcessing, setKycProcessing] = useState('');
  const [kycRejectFor, setKycRejectFor] = useState(null); // student obj
  const [kycRejectReason, setKycRejectReason] = useState('');
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

  const approveKyc = async (studentId) => {
    setKycProcessing(studentId);
    try {
      await api.post(`/students/${studentId}/kyc/approve`);
      toast.success('KYC approved. Student can now download their ID card.');
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setKycProcessing(''); }
  };

  const rejectKyc = async () => {
    if (!kycRejectFor) return;
    if (!kycRejectReason.trim()) { toast.error('Please provide a reason'); return; }
    setKycProcessing(kycRejectFor.id);
    try {
      await api.post(`/students/${kycRejectFor.id}/kyc/reject`, { reason: kycRejectReason.trim() });
      toast.success('KYC rejected. Student has been emailed.');
      setKycRejectFor(null); setKycRejectReason('');
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setKycProcessing(''); }
  };

  const handleVideoUpload = async (e, lessonIdx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 * 1024) { toast.error('Video too large (max 2GB)'); return; }
    setUploadingIndex(lessonIdx);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_post_recorded', String(courseForm.lessons[lessonIdx]?.is_post_recorded || false));
      fd.append('session_date', courseForm.lessons[lessonIdx]?.session_date || '');
      const { data } = await api.post('/academy/videos/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded / (p.total || file.size)) * 100)),
      });
      const arr = [...courseForm.lessons];
      arr[lessonIdx].video_storage_path = data.storage_path;
      arr[lessonIdx].video_original_name = data.original_filename;
      arr[lessonIdx].video_size_mb = data.size_mb;
      setCourseForm({ ...courseForm, lessons: arr });
      toast.success(`Uploaded ${data.original_filename} (${data.size_mb}MB)`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Upload failed');
    } finally {
      setUploadingIndex(-1);
      setUploadProgress(0);
    }
  };

  const reload = useCallback(async () => {
    try {
      const [cRes, eRes, sRes, aRes, kRes] = await Promise.all([
        api.get('/courses'),
        api.get('/students/enrollments'),
        api.get('/students'),
        api.get('/academy/analytics').catch(() => ({ data: null })),
        api.get('/students/kyc/pending').catch(() => ({ data: [] })),
      ]);
      setCourses(cRes.data || []);
      setEnrollments(eRes.data || []);
      setStudents(sRes.data || []);
      setAnalytics(aRes.data);
      setKycPending(kRes.data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // ─── Course CRUD ───
  const openNewCourse = () => { setEditingCourse(null); setCourseForm(EMPTY_COURSE); setCourseDialog(true); };
  const openEditCourse = (c) => {
    setEditingCourse(c);
    setCourseForm({
      title: c.title || '', description: c.description || '',
      category: c.category || 'trading', level: c.level || 'beginner',
      fee_amount: c.fee_amount ?? 0, duration_weeks: c.duration_weeks ?? 4,
      instructor: c.instructor || '', thumbnail_url: c.thumbnail_url || '',
      syllabus: c.syllabus?.length ? c.syllabus : [''],
      lessons: c.lessons?.length ? c.lessons.map(l => ({ ...EMPTY_LESSON, ...l })) : [{ ...EMPTY_LESSON }],
      exam_questions: c.exam_questions?.length ? c.exam_questions.map(q => ({ ...EMPTY_QUESTION, ...q, options: q.options?.length === 4 ? q.options : ['', '', '', ''] })) : [{ ...EMPTY_QUESTION }],
      pass_percentage: c.pass_percentage ?? 60, status: c.status || 'published',
    });
    setCourseDialog(true);
  };

  const saveCourse = async () => {
    if (!courseForm.title.trim()) { toast.error('Title is required'); return; }
    setSavingCourse(true);
    try {
      const payload = {
        ...courseForm,
        fee_amount: parseFloat(courseForm.fee_amount) || 0,
        duration_weeks: parseInt(courseForm.duration_weeks) || 4,
        pass_percentage: parseInt(courseForm.pass_percentage) || 60,
        syllabus: courseForm.syllabus.filter(s => s.trim()),
        lessons: courseForm.lessons.filter(l => l.title?.trim()),
        exam_questions: courseForm.exam_questions.filter(q => q.q?.trim() && q.options.filter(o => o.trim()).length >= 2),
      };
      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}`, payload);
        toast.success('Course updated');
      } else {
        await api.post('/courses', payload);
        toast.success('Course created');
      }
      setCourseDialog(false);
      reload();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingCourse(false);
    }
  };

  const deleteCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success('Course deleted');
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // ─── Enrollment ops ───
  const approveEnrollment = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/students/enrollments/${id}/approve`);
      toast.success('Enrollment activated');
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setProcessing(''); }
  };
  const rejectEnrollment = async (id) => {
    if (!window.confirm('Reject / cancel this enrollment?')) return;
    setProcessing(id);
    try {
      await api.post(`/students/enrollments/${id}/reject`);
      toast.success('Enrollment cancelled');
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setProcessing(''); }
  };

  // ─── Create Student ───
  const createStudent = async () => {
    if (!newStudent.name.trim() || !newStudent.email.trim()) { toast.error('Name and email are required'); return; }
    setCreatingStudent(true);
    try {
      const { data } = await api.post('/students/admin-create', newStudent);
      setCreatedCred(data);
      setNewStudent({ name: '', email: '', phone: '', send_email: true });
      reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setCreatingStudent(false); }
  };

  const filteredEnrollments = enrollments.filter(e => enrollFilter === 'all' || e.status === enrollFilter);
  const pendingCount = enrollments.filter(e => e.status === 'payment_pending').length;

  return (
    <div className="space-y-6" data-testid="academy-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight flex items-center gap-2">
            <GraduationCap size={28} className="text-brand-primary" /> Quantora Academy
          </h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage courses, enrollments, students & certificates</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openNewCourse} className="bg-brand-primary text-white rounded-sm" data-testid="new-course-btn"><Plus size={16} className="mr-1" /> New Course</Button>
          <Button onClick={() => setCreateStudentDialog(true)} variant="outline" className="border-brand-border rounded-sm" data-testid="new-student-btn"><Users size={16} className="mr-1" /> Add Student</Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: 'Courses', v: courses.length, i: BookOpen, c: 'text-brand-primary' },
          { l: 'Total Students', v: students.length, i: Users, c: 'text-brand-secondary' },
          { l: 'Active Enrollments', v: enrollments.filter(e => e.status === 'active').length, i: PlayCircle, c: 'text-brand-success' },
          { l: 'Pending Approval', v: pendingCount, i: Clock, c: 'text-amber-500' },
        ].map(s => (
          <Card key={s.l} className="border-brand-border bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{s.l}</p>
                  <p className="font-heading text-2xl font-bold text-brand-text-primary mt-1">{s.v}</p>
                </div>
                <s.i size={22} className={s.c} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-brand-border rounded-sm p-1 h-auto">
          <TabsTrigger value="courses" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-courses">
            Courses ({courses.length})
          </TabsTrigger>
          <TabsTrigger value="enrollments" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-enrollments">
            Enrollments {pendingCount > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[9px] border-0 h-4 px-1">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="students" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-students">
            Students ({students.length})
          </TabsTrigger>
          <TabsTrigger value="kyc" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-kyc">
            <ShieldCheck size={13} className="mr-1" /> KYC {kycPending.length > 0 && <Badge className="ml-1.5 bg-rose-500 text-white text-[9px] border-0 h-4 px-1">{kycPending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-analytics">
            <BarChart3 size={13} className="mr-1" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* COURSES TAB */}
        <TabsContent value="courses" className="mt-4">
          {loading ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-10 text-center"><Loader2 className="animate-spin text-brand-primary inline" /></CardContent></Card>
          ) : courses.length === 0 ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-12 text-center">
              <BookOpen size={40} className="text-brand-text-secondary mx-auto mb-3" />
              <p className="text-brand-text-primary font-semibold">No courses yet</p>
              <p className="text-sm text-brand-text-secondary mt-1">Click &quot;New Course&quot; to create your first one.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map(c => (
                <Card key={c.id} className="border-brand-border bg-white hover:shadow-md transition-shadow" data-testid={`course-card-${c.id}`}>
                  {c.thumbnail_url && <div className="h-24 bg-cover bg-center rounded-t-lg" style={{ backgroundImage: `url(${c.thumbnail_url})` }} />}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <Badge className="text-[9px] border-0 bg-amber-100 text-amber-700 uppercase mb-1">{c.level}</Badge>
                        <h3 className="font-heading text-base font-bold text-brand-text-primary truncate">{c.title}</h3>
                      </div>
                      <Badge className={`text-[9px] border-0 ${c.status === 'published' ? 'bg-brand-success/15 text-brand-success' : 'bg-slate-100 text-slate-500'}`}>{c.status}</Badge>
                    </div>
                    <p className="text-xs text-brand-text-secondary line-clamp-2 mb-3">{c.description}</p>
                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] text-brand-text-secondary mb-3">
                      <div><p className="font-heading text-sm font-bold text-brand-primary">${c.fee_amount}</p><p>Fee</p></div>
                      <div><p className="font-heading text-sm font-bold text-brand-text-primary">{c.lessons?.length || 0}</p><p>Lessons</p></div>
                      <div><p className="font-heading text-sm font-bold text-brand-text-primary">{c.exam_questions?.length || 0}</p><p>Questions</p></div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEditCourse(c)} className="flex-1 h-8 text-xs" data-testid={`edit-course-${c.id}`}><Pencil size={12} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCourse(c)} className="h-8 text-xs text-brand-error hover:text-brand-error" data-testid={`delete-course-${c.id}`}><Trash2 size={12} /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ENROLLMENTS TAB */}
        <TabsContent value="enrollments" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-widest text-brand-text-secondary font-semibold">Filter:</span>
            {['all', 'payment_pending', 'active', 'completed', 'cancelled'].map(s => (
              <button key={s} onClick={() => setEnrollFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${enrollFilter === s ? 'bg-brand-primary text-white' : 'bg-white border border-brand-border text-brand-text-secondary hover:bg-brand-bg'}`} data-testid={`enroll-filter-${s}`}>
                {s.replace('_', ' ')} ({s === 'all' ? enrollments.length : enrollments.filter(e => e.status === s).length})
              </button>
            ))}
          </div>

          <Card className="border-brand-border bg-white">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="enrollments-table">
                  <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                    {['Student', 'Course', 'Fee', 'Payment', 'Status', 'Enrolled', 'Actions'].map(h => (
                      <th key={h} className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredEnrollments.length === 0 ? (
                      <tr><td colSpan={7} className="py-10 text-center text-brand-text-secondary">No enrollments</td></tr>
                    ) : filteredEnrollments.map(e => (
                      <tr key={e.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover" data-testid={`enrollment-row-${e.id}`}>
                        <td className="py-2.5 px-4">
                          <p className="font-semibold text-brand-text-primary">{e.student_name}</p>
                          <p className="text-[11px] text-brand-text-secondary">{e.student_email || '-'}</p>
                        </td>
                        <td className="py-2.5 px-4 text-brand-text-primary">{e.course_title}</td>
                        <td className="py-2.5 px-4 font-mono font-semibold text-brand-primary">${e.fee_amount?.toFixed(2)}</td>
                        <td className="py-2.5 px-4 text-xs text-brand-text-secondary">
                          <span className="font-medium">{e.payment_mode?.toUpperCase()}</span>
                          {e.payment_reference && <p className="text-[10px] font-mono truncate max-w-[120px]">{e.payment_reference}</p>}
                        </td>
                        <td className="py-2.5 px-4"><Badge className={`text-[10px] border-0 ${statusColor(e.status)}`}>{e.status?.replace('_', ' ')}</Badge></td>
                        <td className="py-2.5 px-4 text-[11px] text-brand-text-secondary">{(e.enrolled_at || '').slice(0, 10)}</td>
                        <td className="py-2.5 px-4">
                          {e.status === 'payment_pending' ? (
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => approveEnrollment(e.id)} disabled={processing === e.id} className="h-7 bg-brand-success text-white text-xs px-2" data-testid={`approve-${e.id}`}>
                                {processing === e.id ? <Loader2 size={11} className="animate-spin" /> : <><CheckCircle2 size={11} className="mr-1" /> Approve</>}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => rejectEnrollment(e.id)} disabled={processing === e.id} className="h-7 text-xs text-brand-error border-brand-error/40 hover:bg-brand-error/5 px-2" data-testid={`reject-${e.id}`}>
                                <XCircle size={11} />
                              </Button>
                            </div>
                          ) : e.status === 'active' ? (
                            <Button size="sm" variant="ghost" onClick={() => rejectEnrollment(e.id)} className="h-7 text-xs text-brand-error hover:bg-brand-error/5">Cancel</Button>
                          ) : (
                            <span className="text-[11px] text-brand-text-secondary">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STUDENTS TAB */}
        <TabsContent value="students" className="mt-4">
          <Card className="border-brand-border bg-white">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="students-table">
                  <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                    {['ID', 'Name', 'Email', 'Phone', 'Status', 'Joined'].map(h => (
                      <th key={h} className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr><td colSpan={6} className="py-10 text-center text-brand-text-secondary">No students yet — click &quot;Add Student&quot; to create one</td></tr>
                    ) : students.map(s => (
                      <tr key={s.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover cursor-pointer" onClick={() => navigate(`/academy/students/${s.id}`)} data-testid={`student-row-${s.id}`}>
                        <td className="py-2.5 px-4 font-mono font-bold text-brand-primary text-xs">{s.student_id}</td>
                        <td className="py-2.5 px-4 font-medium text-brand-text-primary">{s.name}</td>
                        <td className="py-2.5 px-4 text-brand-text-secondary text-xs">{s.email}</td>
                        <td className="py-2.5 px-4 text-brand-text-secondary text-xs">{s.phone || '-'}</td>
                        <td className="py-2.5 px-4"><Badge className={`text-[10px] border-0 ${s.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-slate-100 text-slate-500'}`}>{s.status}</Badge></td>
                        <td className="py-2.5 px-4 text-[11px] text-brand-text-secondary">{(s.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* KYC APPROVAL TAB */}
        <TabsContent value="kyc" className="mt-4 space-y-3" data-testid="kyc-panel">
          {kycPending.length === 0 ? (
            <Card className="border-brand-border bg-white">
              <CardContent className="p-10 text-center">
                <ShieldCheck size={40} className="text-brand-success/60 mx-auto mb-3" />
                <p className="text-brand-text-primary font-semibold">All caught up</p>
                <p className="text-sm text-brand-text-secondary mt-1">There are no KYC applications awaiting review right now.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {kycPending.map(k => (
                <Card key={k.id} className="border-brand-border bg-white" data-testid={`kyc-card-${k.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {k.kyc_photo_path ? (
                        <img
                          src={`${apiBase}/students/kyc/photo?path=${encodeURIComponent(k.kyc_photo_path)}`}
                          alt={k.name}
                          className="w-24 h-24 object-cover rounded-lg border border-brand-border shrink-0"
                          data-testid={`kyc-photo-${k.id}`}
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-brand-bg border border-dashed border-brand-border flex items-center justify-center text-brand-text-secondary shrink-0">
                          <IdCard size={22} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-heading text-base font-bold text-brand-text-primary">{k.kyc_full_name || k.name}</p>
                            <p className="text-xs font-mono text-brand-text-secondary">{k.student_id} · {k.email}</p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">PENDING</Badge>
                        </div>
                        <div className="mt-2 text-[11px] space-y-0.5 text-brand-text-primary">
                          <div><span className="text-brand-text-secondary">DOB:</span> {k.kyc_dob || '-'}   <span className="text-brand-text-secondary ml-2">Gender:</span> {(k.kyc_gender || '-').toUpperCase()}</div>
                          <div><span className="text-brand-text-secondary">Phone:</span> {k.phone || '-'}   {k.kyc_parent_guardian && <><span className="text-brand-text-secondary ml-2">Guardian:</span> {k.kyc_parent_guardian}</>}</div>
                          <div><span className="text-brand-text-secondary">Address:</span> {k.kyc_address || '-'}, {k.kyc_state} {k.kyc_pincode}</div>
                          <div className="font-mono"><span className="font-sans text-brand-text-secondary">{(k.kyc_id_type || '').toUpperCase()}:</span> {k.kyc_id_number}</div>
                          <div className="text-brand-text-secondary text-[10px] mt-1">Submitted {(k.kyc_submitted_at || '').slice(0, 16).replace('T', ' ')}</div>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveKyc(k.id)}
                            disabled={kycProcessing === k.id}
                            className="bg-brand-success text-white text-xs h-8"
                            data-testid={`kyc-approve-${k.id}`}
                          >
                            {kycProcessing === k.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => { setKycRejectFor(k); setKycRejectReason(''); }}
                            disabled={kycProcessing === k.id}
                            className="text-xs h-8 text-rose-600 border-rose-200 hover:bg-rose-50"
                            data-testid={`kyc-reject-${k.id}`}
                          >
                            <XCircle size={12} className="mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics" className="mt-4 space-y-4" data-testid="analytics-panel">
          {!analytics ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-10 text-center text-brand-text-secondary">Loading analytics...</CardContent></Card>
          ) : (
            <>
              {/* Headline totals */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {[
                  { l: 'Revenue', v: `$${analytics.totals.revenue_usd.toLocaleString()}`, i: DollarSign, c: 'text-brand-success' },
                  { l: 'Enrollments', v: analytics.totals.enrollments, i: BookOpen, c: 'text-brand-primary' },
                  { l: 'Certified', v: analytics.totals.certificates_issued, i: Award, c: 'text-amber-500' },
                  { l: 'Completed', v: analytics.totals.completed, i: CheckCircle2, c: 'text-brand-secondary' },
                  { l: 'Cancelled', v: analytics.totals.cancelled, i: XCircle, c: 'text-brand-error' },
                ].map(s => (
                  <Card key={s.l} className="border-brand-border bg-white"><CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{s.l}</p>
                        <p className="font-heading text-xl font-bold text-brand-text-primary mt-1">{s.v}</p>
                      </div>
                      <s.i size={20} className={s.c} />
                    </div>
                  </CardContent></Card>
                ))}
              </div>

              {/* Enrollment funnel */}
              <Card className="border-brand-border bg-white">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Enrollment Funnel</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="funnel-stages">
                    {[
                      { l: 'Registered', v: analytics.funnel.registered, c: 'from-slate-400 to-slate-600' },
                      { l: 'Enrolled', v: analytics.funnel.enrolled_any, c: 'from-blue-400 to-blue-600' },
                      { l: 'Activated', v: analytics.funnel.activated, c: 'from-emerald-400 to-emerald-600' },
                      { l: 'Completed', v: analytics.funnel.completed, c: 'from-purple-400 to-purple-600' },
                      { l: 'Certified', v: analytics.funnel.certified, c: 'from-amber-400 to-amber-600' },
                    ].map((s, i, arr) => {
                      const first = arr[0].v || 1;
                      const pct = Math.round((s.v / first) * 100);
                      return (
                        <div key={s.l} className={`rounded-lg p-3 text-white bg-gradient-to-br ${s.c}`}>
                          <p className="text-[10px] uppercase tracking-widest opacity-80">{s.l}</p>
                          <p className="font-heading text-2xl font-bold mt-1">{s.v}</p>
                          <p className="text-[10px] opacity-80 mt-1">{pct}% of registered</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Per-course breakdown */}
              <Card className="border-brand-border bg-white">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Course Performance</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="per-course-table">
                      <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                        {['Course', 'Level', 'Fee', 'Total', 'Active', 'Completed', 'Pending', 'Certs', 'Avg Score', 'Completion %'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {analytics.per_course.length === 0 ? (
                          <tr><td colSpan={10} className="py-10 text-center text-brand-text-secondary">No course data yet.</td></tr>
                        ) : analytics.per_course.map(c => (
                          <tr key={c.course_id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover" data-testid={`per-course-row-${c.course_id}`}>
                            <td className="py-2 px-3 font-medium text-brand-text-primary">{c.title}</td>
                            <td className="py-2 px-3"><Badge className="text-[9px] border-0 bg-amber-100 text-amber-700 uppercase">{c.level}</Badge></td>
                            <td className="py-2 px-3 font-mono">${c.fee_amount}</td>
                            <td className="py-2 px-3 font-bold">{c.total_enrollments}</td>
                            <td className="py-2 px-3 text-brand-success">{c.active}</td>
                            <td className="py-2 px-3 text-brand-primary">{c.completed}</td>
                            <td className="py-2 px-3 text-amber-600">{c.pending}</td>
                            <td className="py-2 px-3 text-amber-600 font-bold">{c.certificates_issued}</td>
                            <td className="py-2 px-3 font-mono">{c.avg_exam_score}%</td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-brand-bg overflow-hidden max-w-[100px]">
                                  <div className="h-full bg-brand-primary" style={{ width: `${c.completion_rate}%` }} />
                                </div>
                                <span className="font-mono text-xs text-brand-text-primary">{c.completion_rate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ─── COURSE BUILDER DIALOG ─── */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="course-dialog">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'New Course'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title *</Label>
              <Input value={courseForm.title} onChange={e => setCourseForm({ ...courseForm, title: e.target.value })} data-testid="course-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Instructor</Label>
              <Input value={courseForm.instructor} onChange={e => setCourseForm({ ...courseForm, instructor: e.target.value })} placeholder="e.g. Pintu Sk" />
            </div>
            <div className="space-y-1.5 lg:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={courseForm.description} onChange={e => setCourseForm({ ...courseForm, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={courseForm.category} onValueChange={v => setCourseForm({ ...courseForm, category: v })}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Level</Label>
              <Select value={courseForm.level} onValueChange={v => setCourseForm({ ...courseForm, level: v })}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fee (USD)</Label>
              <Input type="number" value={courseForm.fee_amount} onChange={e => setCourseForm({ ...courseForm, fee_amount: e.target.value })} data-testid="course-fee" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Duration (weeks)</Label>
              <Input type="number" value={courseForm.duration_weeks} onChange={e => setCourseForm({ ...courseForm, duration_weeks: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Thumbnail URL</Label>
              <Input value={courseForm.thumbnail_url} onChange={e => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={courseForm.status} onValueChange={v => setCourseForm({ ...courseForm, status: v })}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Pass %</Label>
              <Input type="number" min="0" max="100" value={courseForm.pass_percentage} onChange={e => setCourseForm({ ...courseForm, pass_percentage: e.target.value })} />
            </div>
          </div>

          {/* SYLLABUS */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Syllabus (topics)</Label>
              <Button size="sm" variant="ghost" onClick={() => setCourseForm({ ...courseForm, syllabus: [...courseForm.syllabus, ''] })} className="h-7 text-xs"><Plus size={12} className="mr-1" /> Add topic</Button>
            </div>
            <div className="space-y-1.5">
              {courseForm.syllabus.map((s, i) => (
                <div key={`syl-${i}`} className="flex gap-2 items-center">
                  <span className="text-xs text-brand-text-secondary w-6">{i + 1}.</span>
                  <Input value={s} onChange={e => { const arr = [...courseForm.syllabus]; arr[i] = e.target.value; setCourseForm({ ...courseForm, syllabus: arr }); }} className="h-8 text-sm" />
                  <Button size="icon" variant="ghost" onClick={() => setCourseForm({ ...courseForm, syllabus: courseForm.syllabus.filter((_, j) => j !== i) })} className="h-8 w-8 text-brand-error"><X size={14} /></Button>
                </div>
              ))}
            </div>
          </div>

          {/* LESSONS */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Lessons</Label>
              <Button size="sm" variant="ghost" onClick={() => setCourseForm({ ...courseForm, lessons: [...courseForm.lessons, { ...EMPTY_LESSON }] })} className="h-7 text-xs"><Plus size={12} className="mr-1" /> Add lesson</Button>
            </div>
            <div className="space-y-2">
              {courseForm.lessons.map((l, i) => (
                <div key={`les-${i}`} className="border border-brand-border/60 rounded-lg p-3 space-y-2 bg-brand-bg/40">
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-brand-text-secondary w-8">L{i + 1}</span>
                    <Input placeholder="Lesson title" value={l.title} onChange={e => { const arr = [...courseForm.lessons]; arr[i].title = e.target.value; setCourseForm({ ...courseForm, lessons: arr }); }} className="h-8 text-sm" data-testid={`lesson-title-${i}`} />
                    <Input placeholder="Duration (min)" type="number" value={l.duration_min || 15} onChange={e => { const arr = [...courseForm.lessons]; arr[i].duration_min = parseInt(e.target.value) || 0; setCourseForm({ ...courseForm, lessons: arr }); }} className="h-8 text-sm w-28" />
                    <Button size="icon" variant="ghost" onClick={() => setCourseForm({ ...courseForm, lessons: courseForm.lessons.filter((_, j) => j !== i) })} className="h-8 w-8 text-brand-error"><X size={14} /></Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 pl-10 items-center">
                    <Select value={l.video_type || 'youtube'} onValueChange={(v) => { const arr = [...courseForm.lessons]; arr[i].video_type = v; setCourseForm({ ...courseForm, lessons: arr }); }}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`lesson-vtype-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="upload">Uploaded</SelectItem>
                        <SelectItem value="external">External URL</SelectItem>
                      </SelectContent>
                    </Select>
                    {(l.video_type === 'youtube' || l.video_type === 'external' || !l.video_type) ? (
                      <Input placeholder={l.video_type === 'external' ? 'https://... (mp4/webm)' : 'YouTube embed URL'} value={l.video_url || ''} onChange={e => { const arr = [...courseForm.lessons]; arr[i].video_url = e.target.value; setCourseForm({ ...courseForm, lessons: arr }); }} className="h-8 text-sm col-span-2" />
                    ) : (
                      <div className="col-span-2 flex gap-2 items-center">
                        <input type="file" accept="video/*" onChange={(e) => handleVideoUpload(e, i)} className="hidden" id={`video-upload-${i}`} data-testid={`video-upload-input-${i}`} />
                        <label htmlFor={`video-upload-${i}`} className="flex-1 cursor-pointer">
                          <div className="h-8 border border-dashed border-brand-border rounded flex items-center justify-center text-xs text-brand-text-secondary hover:bg-brand-bg/60">
                            {uploadingIndex === i ? <><Loader2 size={12} className="animate-spin mr-1" /> {uploadProgress}%</> : l.video_storage_path ? <span className="truncate px-2">✓ {(l.video_original_name || 'video uploaded').slice(0, 30)}</span> : <><Upload size={12} className="mr-1" /> Choose video</>}
                          </div>
                        </label>
                      </div>
                    )}
                    <Input placeholder="PDF URL (optional)" value={l.pdf_url || ''} onChange={e => { const arr = [...courseForm.lessons]; arr[i].pdf_url = e.target.value; setCourseForm({ ...courseForm, lessons: arr }); }} className="h-8 text-sm" />
                  </div>
                  <div className="flex gap-3 items-center pl-10">
                    <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                      <input type="checkbox" checked={l.is_post_recorded || false} onChange={e => { const arr = [...courseForm.lessons]; arr[i].is_post_recorded = e.target.checked; setCourseForm({ ...courseForm, lessons: arr }); }} className="accent-brand-primary" data-testid={`lesson-post-recorded-${i}`} />
                      <span>Post-recorded (live session)</span>
                    </label>
                    {l.is_post_recorded && (
                      <>
                        <Label className="text-[11px] text-brand-text-secondary">Session date:</Label>
                        <Input type="date" value={l.session_date || ''} onChange={e => { const arr = [...courseForm.lessons]; arr[i].session_date = e.target.value; setCourseForm({ ...courseForm, lessons: arr }); }} className="h-7 text-xs w-40" />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* EXAM QUESTIONS */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Exam Questions (MCQ)</Label>
              <Button size="sm" variant="ghost" onClick={() => setCourseForm({ ...courseForm, exam_questions: [...courseForm.exam_questions, { ...EMPTY_QUESTION }] })} className="h-7 text-xs"><Plus size={12} className="mr-1" /> Add question</Button>
            </div>
            <div className="space-y-2">
              {courseForm.exam_questions.map((q, qi) => (
                <div key={`q-${qi}`} className="border border-brand-border/60 rounded-lg p-3 space-y-2 bg-brand-bg/40">
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-brand-text-secondary w-8 font-bold">Q{qi + 1}</span>
                    <Input placeholder="Question" value={q.q} onChange={e => { const arr = [...courseForm.exam_questions]; arr[qi].q = e.target.value; setCourseForm({ ...courseForm, exam_questions: arr }); }} className="h-8 text-sm" data-testid={`question-${qi}`} />
                    <Button size="icon" variant="ghost" onClick={() => setCourseForm({ ...courseForm, exam_questions: courseForm.exam_questions.filter((_, j) => j !== qi) })} className="h-8 w-8 text-brand-error"><X size={14} /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pl-10">
                    {q.options.map((opt, oi) => (
                      <div key={`opt-${qi}-${oi}`} className="flex items-center gap-2">
                        <input type="radio" name={`correct-${qi}`} checked={q.correct_index === oi} onChange={() => { const arr = [...courseForm.exam_questions]; arr[qi].correct_index = oi; setCourseForm({ ...courseForm, exam_questions: arr }); }} className="accent-brand-success" title="Mark correct" />
                        <Input placeholder={`Option ${oi + 1}${q.correct_index === oi ? ' ✓' : ''}`} value={opt} onChange={e => { const arr = [...courseForm.exam_questions]; arr[qi].options[oi] = e.target.value; setCourseForm({ ...courseForm, exam_questions: arr }); }} className={`h-8 text-sm ${q.correct_index === oi ? 'border-brand-success/60 bg-brand-success/5' : ''}`} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-brand-text-secondary pl-10">Radio marks the correct answer.</p>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialog(false)}>Cancel</Button>
            <Button onClick={saveCourse} disabled={savingCourse} className="bg-brand-primary text-white" data-testid="save-course-btn">
              {savingCourse ? <Loader2 className="animate-spin" size={14} /> : <><ChevronRight size={14} className="mr-1" /> {editingCourse ? 'Save Changes' : 'Create Course'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── CREATE STUDENT DIALOG ─── */}
      <Dialog open={createStudentDialog} onOpenChange={(o) => { setCreateStudentDialog(o); if (!o) setCreatedCred(null); }}>
        <DialogContent className="max-w-md" data-testid="create-student-dialog">
          <DialogHeader>
            <DialogTitle>{createdCred ? 'Student Created' : 'Add New Student'}</DialogTitle>
          </DialogHeader>
          {createdCred ? (
            <div className="space-y-3">
              <div className="bg-brand-success/10 border border-brand-success/30 rounded-lg p-3">
                <p className="text-xs uppercase text-brand-success font-semibold mb-2">Login credentials (also emailed)</p>
                <p className="text-xs">Student ID: <span className="font-mono font-bold">{createdCred.student_id}</span></p>
                <p className="text-xs">Email: <span className="font-mono">{createdCred.email}</span></p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs">Password:</p>
                  <code className="bg-white px-2 py-0.5 rounded text-brand-primary font-mono text-xs" data-testid="new-student-password">{createdCred.temporary_password}</code>
                  <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdCred.temporary_password); toast.success('Copied'); }} className="h-6 w-6"><Copy size={12} /></Button>
                </div>
              </div>
              <p className="text-[11px] text-brand-text-secondary">Share this password with the student. They can log in at <span className="font-mono">/student/login</span>.</p>
              <Button className="w-full" onClick={() => { setCreatedCred(null); setCreateStudentDialog(false); }}>Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Name *</Label>
                <Input value={newStudent.name} onChange={e => setNewStudent({ ...newStudent, name: e.target.value })} data-testid="new-student-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={newStudent.email} onChange={e => setNewStudent({ ...newStudent, email: e.target.value })} data-testid="new-student-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={newStudent.phone} onChange={e => setNewStudent({ ...newStudent, phone: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={newStudent.send_email} onChange={e => setNewStudent({ ...newStudent, send_email: e.target.checked })} className="accent-brand-primary" data-testid="send-email-toggle" />
                <span>Email login credentials to student</span>
              </label>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateStudentDialog(false)}>Cancel</Button>
                <Button onClick={createStudent} disabled={creatingStudent} className="bg-brand-primary text-white" data-testid="create-student-submit">
                  {creatingStudent ? <Loader2 className="animate-spin" size={14} /> : 'Create Student'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KYC REJECT DIALOG */}
      <Dialog open={!!kycRejectFor} onOpenChange={(o) => { if (!o) { setKycRejectFor(null); setKycRejectReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject KYC — {kycRejectFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-brand-text-secondary">The student will be emailed this reason and asked to re-submit.</p>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Textarea
                value={kycRejectReason}
                onChange={e => setKycRejectReason(e.target.value)}
                rows={3}
                placeholder="e.g. Photo is blurred, please upload a clearer one"
                data-testid="kyc-reject-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setKycRejectFor(null); setKycRejectReason(''); }}>Cancel</Button>
            <Button onClick={rejectKyc} disabled={kycProcessing === kycRejectFor?.id} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="kyc-reject-confirm">
              {kycProcessing === kycRejectFor?.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <XCircle size={14} className="mr-1" />}
              Reject KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
